<template>
  <v-row>
    <!-- Studios auswählen -->
    <v-col align="center" cols="12">
      <v-card max-width="500px" dark class="px-5 py-2" flat>
        <v-row no-gutters>
          <v-col cols="9">
            <v-select
              :loading="loading"
              :disabled="loading"
              label="Studios"
              @change="fetchHealthcareRequests"
              :items="availableStudios"
              v-model="selectedStudios"
              multiple
              deletable-chips
              small-chips>
              <template v-slot:prepend-item>
                <v-list-item ripple @click="toggleSelectAll">
                  <v-list-item-action>
                    <v-icon :color="selectedStudios.length > 0 ? 'primary' : ''">
                      {{ selectAllIcon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title> Alle auswählen / abwählen </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
            </v-select>
          </v-col>
          <v-col cols="3">
            <v-select
              @change="fetchHealthcareRequests"
              :loading="loading"
              :disabled="loading"
              label="Status"
              :items="[
                { text: 'Offen', value: 'pending' },
                { text: 'Abgeschlossen', value: 'accepted' },
              ]"
              v-model="selectedStatus"></v-select>
          </v-col>
          <v-col cols="9">
            <v-text-field label="Suche (Vor- oder Nachname)" v-model="searchFilter"></v-text-field>
          </v-col>
        </v-row>
      </v-card>
      <v-divider color="white"></v-divider>
    </v-col>

    <!-- Lade-Spinner -->
    <v-col align="center" cols="12" v-if="loading">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </v-col>

    <!-- Keine Daten gefunden -->
    <v-col align="center" cols="12" v-if="!loading && requestsFiltered.length === 0">
      <v-alert max-width="500px" type="info"
        >Keine Anfragen mit aktuellem Filter gefunden
        <br />
        Studios mit offenen Anfragen: <br />
        <div v-for="(studio, index) in groupedOpenStudios" :key="index" class="d-flex justify-space-between align-center my-2">
          <span>{{ studio.name }}</span>
          <v-chip small>{{ studio.count }}</v-chip>
        </div>
      </v-alert>
    </v-col>

    <v-col align="center" v-for="(requestItem, index) in requestsFiltered" :key="requestItem._id" class="mb-2" xl="4" lg="6" md="6" sm="12" xs="12">
      <v-card class="rounded-lg fill-height" align="left" max-width="500px" dark flat>
        <!-- Beispiel: Studio-ID und Status anzeigen -->
        <v-card-subtitle class="mb-0 pb-0">
          Studio: {{ requestItem.studio.name }} <v-chip small class="ml-2"> {{ $moment(requestItem.createdAt).format("DD.MM.YYYY") }}</v-chip>
        </v-card-subtitle>

        <!-- Titel des Vertrags -->
        <v-card-title class="mt-0 pt-0"> {{ requestItem.user.vorname }} {{ requestItem.user.nachname }} ({{ requestItem.user.email }}) </v-card-title>

        <!-- User/Customer-Info (aktuell haben wir nur die ID) -->
        <v-card-subtitle class="pb-0">
          Vertragstyp:
          {{ requestItem.contract.title }}
        </v-card-subtitle>

        <v-row no-gutters>
          <v-col cols="12">
            <v-row no-gutters>
              <v-col cols="8">
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-card-subtitle>
                      <v-chip small color="primary"> Start: {{ requestItem.contract.start }} </v-chip>
                      <v-chip small color="primary" class="ml-2"> Ende: {{ requestItem.contract.end }} </v-chip>
                    </v-card-subtitle>
                  </v-col>
                </v-row>
              </v-col>

              <!-- "Freischalten"- oder Aktions-Button rechts -->
              <v-col class="d-flex justify-end align-center" cols="4" v-if="requestItem.status === 'pending'">
                <v-btn small @click="setNewSelectedRequest(requestItem)" color="orange" class="mr-5">
                  <v-icon>mdi-lock-open</v-icon>
                </v-btn>
                <v-btn small @click="deleteHealthcareRequest(requestItem._id)" color="red" class="mr-5">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
              <v-col class="d-flex justify-end align-center" cols="4" v-if="requestItem.status === 'accepted'">
                <v-btn @click="createPDF(requestItem)" color="green" class="mr-5">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <!-- Dialog zur Bearbeitung/Freischaltung -->
    <v-dialog max-width="500px" persistent :value="selectedRequest !== null">
      <v-card v-if="selectedRequest && selectedRequest.contract && selectedRequest.contract.title">
        <v-card-title style="background-color: var(--v-primary)" class="white--text">
          {{ selectedRequest.user.vorname }} {{ selectedRequest.user.nachname }} ({{ selectedRequest.user.email }})
        </v-card-title>

        <!-- Beispiel: Alert, falls du bestimmte Zusatz-Logik hast (wurde hier entfernt) -->
        <!-- <v-alert class="ma-5" color="orange" type="info" v-if="???">
                    ...
                </v-alert> -->

        <v-card-title class="h5 mt-2">Kundendaten</v-card-title>
        <v-card-text class="pb-0">
          Studio:
          {{ selectedRequest.studio.name }} <br />
          Vorname: {{ selectedRequest.user.vorname }} <br />
          Nachname: {{ selectedRequest.user.nachname }} <br />
          E-Mail: {{ selectedRequest.user.email }}
        </v-card-text>

        <v-card-title>gefundene Zahlungen</v-card-title>
        <v-row no-gutters style="max-width: 100%">
          <div class="mb-1 d-flex" style="max-width: 100%" v-for="trans of contractTransactions">
            <v-col cols="10">
              <v-card class="pa-1 rounded-xl d-flex">
                <v-col cols="2" class="pa-0 align-content-center">
                  <v-img src="/img/lottiefiles/transactions/typeMembership.png"></v-img>
                </v-col>
                <v-col cols="10">
                  <v-card-subtitle class="pa-0">{{ trans.Description }}</v-card-subtitle>
                  <v-card-subtitle class="pa-0"
                    ><span :class="{ 'red--text': trans.Value.value < 0 }">{{ trans.Value.value + " " + trans.Value.currencyCode }}</span>
                    <span class="red--text">{{ additionalPaymentInfos(trans) }}</span></v-card-subtitle
                  >
                </v-col>
              </v-card>
            </v-col>
            <v-col cols="2"> <v-checkbox hide-details v-model="trans.checked" color="primary"></v-checkbox></v-col>
          </div>
        </v-row>
        <v-card-actions class="justify-content-center"
          ><v-btn color="primary" @click="insertCost()">übernehmen <v-icon>mdi-arrow-down-thick</v-icon></v-btn></v-card-actions
        >
        <!-- Betrag/Kosten -->
        <v-text-field class="mx-5 mt-5" type="number" v-model="selectedRequest.amount" label="Kosten des Abos (KK-Bestätigung)"></v-text-field>
        <v-text-field class="mx-5 mt-5" v-model="selectedRequest.contract.title" label="Abo Titel"></v-text-field>
        <v-checkbox class="mx-5 mt-5" type="number" v-model="selectedRequest.wellness" label="Wellness und oder Bad inkludiert"></v-checkbox>

        <v-card-actions class="justify-space-between">
          <v-btn @click="selectedRequest = null" text>Abbrechen</v-btn>
          <v-btn color="orange" class="white--text" @click="createPDF(selectedRequest)"> Test PDF erstellen </v-btn>
          <v-btn @click="updateContractPayment" color="primary">Freischalten</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import api from "@/api"
import { mapState } from "vuex"
import userService from "@/services/userService"
export default {
  data() {
    return {
      userService,
      api,
      searchFilter: "",
      selectedStatus: "pending",
      loading: true,
      requests: [], // Hält die Array-Response vom Server
      selectedStudios: [], // Vom Nutzer gewählte Studios (IDs oder ggf. Namen)
      selectedRequest: null, // Aktuell ausgewähltes Objekt für den Dialog
      groupedOpenStudios: [], // Studios mit offenen Anfragen
    }
  },
  created() {
    this.selectedStudios.push(this.userService.getStudio()._id)
    this.fetchHealthcareRequests()
  },
  methods: {
    async deleteHealthcareRequest(requestId) {
      if (confirm("Möchtest du diese Anfrage wirklich löschen?")) {
        api
          .deleteHealthcareRequest(requestId)
          .then(() => {
            this.$toast.success("Anfrage gelöscht")
            this.fetchHealthcareRequests()
          })
          .catch((e) => {
            this.$toast.error("Fehler beim Löschen der Anfrage")
          })
      }
    },
    setNewSelectedRequest(requestItem) {
      console.log("hoi")
      let amount = 0
      if (requestItem.contract.title.includes("Mitarbeiter")) {
        requestItem.contract.title = "PREMIUM PLUS"
        amount = 1449
      }

      this.selectedRequest = { ...requestItem, amount }
    },
    toggleSelectAll() {
      if (this.allStudiosSelected) {
        // Wenn alle ausgewählt sind -> Auswahl aufheben
        this.selectedStudios = []
      } else {
        // Sonst -> Alle auswählen
        this.selectedStudios = this.availableStudios.map((studio) => studio.value)
      }
      // Optional: Daten direkt nach der Auswahl neu laden
      // this.fetchHealthcareRequests()
    },
    async createPDF(data) {
      // Da sich das Datenmodell geändert hat,
      // passe deine Payload hier an deine neuen Felder an:
      const request = data
      const formData = {
        test: true,
        contract: {
          wellness: data.wellness,
          ContractID: request.contract.ContractID,
          startDate: request.contract.start,
          endDate: request.contract.end,
          amount: request.amount,
          packages: data.packages,
          membershipName: request.contract.title,
          studioId: request.studio,
          // Das hier sind nur Beispielwerte
          studioPopulated: {
            art: null,
            adresse: null,
            healtcareConfirmationNumber: null,
            logo: null,
            website: null,
          },
        },
        customerId: request.user,
        // Falls du Kundendetails hast, bitte hier anpassen.
        customerInfos: {
          healthcareName: "Testversicherung",
          additionalInsurance: "Testzusatzversicherung",
          insuranceNumber: "Testversicherungsnummer",
          firstName: "Vorname",
          lastName: "Nachname",
          street: "Strasse",
          zip: "PLZ",
          city: "Stadt",
          signature: null,
        },
      }

      try {
        this.$toast.success("PDF wird erstellt")
        await api.createHealthcarePDF(formData)
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Herunterladen, bitte versuche es erneut")
      }
    },
    additionalPaymentInfos(trans) {
      if (!this.selectedRequest.contract.yearCount) return ""

      return " / " + this.selectedRequest.contract.yearCount + " = " + Number(trans.Value.value) / this.selectedRequest.contract.yearCount + " CHF"
    },
    /**
     * Beispiel-Funktion zum Freischalten / Payment updaten
     */
    async updateContractPayment() {
      if (this.selectedRequest?.amount > 0 && this.selectedRequest?._id && this.selectedRequest?.user) {
        try {
          await api.updateHealthcareRequest({
            amount: this.selectedRequest.amount,
            packages: this.selectedRequest.packages,
            wellness: this.selectedRequest.wellness,
            status: "accepted",
            title: this.selectedRequest.contract.title,
            requestId: this.selectedRequest._id,
          })
          this.$toast.success("Status aktualisiert")
          this.selectedRequest = null
          this.requests = []
          this.fetchHealthcareRequests()
        } catch (e) {
          console.error(e)
          this.$toast.error("Fehler beim Aktualisieren")
        }
      } else {
        this.$toast.error("Fülle alle Felder aus")
      }
    },

    /**
     * Server-Abfrage
     */
    async fetchHealthcareRequests() {
      this.loading = true
      try {
        const response = await api.fetchHealthcareRequestsAdmin(this.selectedStatus, this.selectedStudios)
        this.requests = response.requests

        // Gruppiere Studios mit offenen Anfragen
        const studioCounts = {}
        for (const request of response.openStuds) {
          const studioName = request.studio.name
          if (!studioCounts[studioName]) {
            studioCounts[studioName] = 0
          }
          studioCounts[studioName]++
        }

        // Konvertiere in ein Array für die Anzeige
        this.groupedOpenStudios = Object.entries(studioCounts).map(([name, count]) => ({
          name,
          count,
        }))
        console.log("Gruppierte Studios mit offenen Anfragen:", this.groupedOpenStudios)
      } catch (error) {
        console.error(error)
        this.$error("Fehler beim Laden der Anfragen")
      } finally {
        this.loading = false
      }
    },
    insertCost() {
      let price = 0
      for (let transaction of this.contractTransactions) {
        if (transaction.checked) {
          let divider = 1

          if (this.selectedRequest.contract.yearCount) {
            divider = this.selectedRequest.contract.yearCount
          }

          price += Number(transaction.Value.value / divider)
        }
      }
      this.selectedRequest.amount = price
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    allStudiosSelected() {
      // Überprüft, ob alle verfügbaren Studios ausgewählt sind
      return this.selectedStudios.length === this.availableStudios.length
    },

    someStudiosSelected() {
      // Überprüft, ob einige, aber nicht alle Studios ausgewählt sind
      return this.selectedStudios.length > 0 && !this.allStudiosSelected
    },

    selectAllIcon() {
      // Gibt das passende Icon basierend auf der Auswahl zurück
      if (this.allStudiosSelected) return "mdi-close-box"
      if (this.someStudiosSelected) return "mdi-minus-box"
      return "mdi-checkbox-blank-outline"
    },
    requestsFiltered() {
      if (!this.requests.length) {
        return []
      }

      let filteredRequests = this.requests

      // Falls der Nutzer keine Studios ausgewählt hat, zeig alle
      if (this.searchFilter) {
        return this.requests.filter(
          (req) =>
            req.user.vorname.toLowerCase().includes(this.searchFilter.toLowerCase()) ||
            (req.user.vorname.toLowerCase() + " " + req.user.nachname.toLowerCase()).includes(this.searchFilter.toLowerCase()) ||
            req.user.nachname.toLowerCase().includes(this.searchFilter.toLowerCase()) ||
            req.user.email.toLowerCase().includes(this.searchFilter.toLowerCase())
        )
      }

      return filteredRequests
    },

    /**
     * Liste aller verfügbaren Studios (IDs),
     * abgeleitet aus den Daten.
     */
    availableStudios() {
      console.log("da sind alli studios", this.storeStudios)
      if (!this.storeStudios) return []
      console.log("de logg machi jetzt no einisch")
      return this.storeStudios
        .filter((studio) => studio.code)
        .map((studio) => {
          return {
            text: studio.name,
            value: studio._id,
          }
        })
      const studioIds = this.requests.map((req) => req.studio.name).filter((id) => !!id)
      return [...new Set(studioIds)]
    },
    contractTransactions() {
      if (!this.selectedRequest.contract.transactions) return []

      if (
        this.selectedRequest.contract.title.includes("AHV") ||
        this.selectedRequest.contract.title.includes("Erwachsene") ||
        this.selectedRequest.contract.title.includes("PREMIUM") ||
        this.selectedRequest.contract.title.includes("Premium")
      ) {
        this.selectedRequest.wellness = true
      }
      this.selectedRequest.packages = ""
      let allTrans = []

      for (let transaction of this.selectedRequest.contract.transactions) {
        for (let detTransaction of transaction.det.AccountTransactionDetail) {
          if (detTransaction.Description.includes("Einschreib") || detTransaction.Description.includes("Aktivierungs")) {
            continue
          } else {
            if (detTransaction.Description.includes("Wellness") || detTransaction.Description.includes("Spa")) {
              this.selectedRequest.wellness = true
            }

            detTransaction.checked = true
            allTrans.push(detTransaction)
          }
        }
      }
      console.log("hoi", allTrans)
      return allTrans
    },
  },
}
</script>

<style lang="scss" scoped>
.studio-name {
  font-weight: bold;
}

.time {
  font-size: 12px;
  color: #999;
}

.contract-name {
  font-style: italic;
}
</style>
